import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Shows every unsubscribed contact across ALL campaigns, deduped by email.
 * Admin can permanently REMOVE with a confirmation dialog (double-check because deletion is destructive).
 */
export default function UnsubscribeListCard() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetch_ = async () => {
    try {
      const r = await api.get("/admin/unsubscribes");
      setRows(r.data?.unsubscribes || []);
    } catch {
      setRows(null);
    }
  };

  useEffect(() => {
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, []);

  const removeForever = async (email) => {
    const first = window.confirm(`Permanently REMOVE ${email} from every campaign list?\n\n· Deletes them from Volunteer Recruitment contacts\n· Deletes them from Post-Pitch contacts\n· They will NOT show in any admin list anymore\n· Suppression is safer if you want to keep an audit trail\n\nContinue?`);
    if (!first) return;
    const second = window.confirm(`Final check — REALLY delete ${email}?\n\nThis cannot be undone. If they get added again via CSV or add-contact form, they will be treated as a new contact and could receive emails again.`);
    if (!second) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/remove-everywhere?email=${encodeURIComponent(email)}`);
      alert(`✅ Removed ${email}\n\nCampaign contacts deleted: ${r.data.campaign_contacts_deleted}\nPost-pitch contacts deleted: ${r.data.postpitch_contacts_deleted}`);
      await fetch_();
    } catch (e) {
      alert(`❌ Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const manualSuppress = async () => {
    const email = window.prompt("Add an email to the unsubscribe list (suppress across all campaigns)?");
    if (!email) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/unsubscribe-everywhere?email=${encodeURIComponent(email)}`);
      alert(`✅ Suppressed ${email}\n\nCampaign contacts affected: ${r.data.campaign_contacts}\nPost-pitch contacts affected: ${r.data.postpitch_contacts}`);
      await fetch_();
    } catch (e) {
      alert(`❌ Failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  if (rows === null) return null;

  const cap = expanded ? rows.length : 5;
  const visible = rows.slice(0, cap);

  const fmtTs = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString([], { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <div data-testid="unsubscribe-list" className="rounded-2xl border border-slate-200 bg-white shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-slate-50">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold font-[Outfit] px-2.5 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-300">
            <span className={`h-1.5 w-1.5 rounded-full ${rows.length ? "bg-slate-500" : "bg-emerald-500"}`} />
            Unsubscribe List
          </span>
          <span className="font-[Outfit] text-sm text-slate-800 font-semibold">
            {rows.length} {rows.length === 1 ? "person has" : "people have"} unsubscribed
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="unsub-manual-add" onClick={manualSuppress} disabled={busy} variant="outline" className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 font-[Outfit] h-8 text-xs">
            + Manually Add
          </Button>
          {rows.length > 5 && (
            <button onClick={() => setExpanded((v) => !v)} data-testid="unsub-toggle-all" className="text-[11px] font-[Outfit] font-semibold text-slate-700 hover:text-slate-900">
              {expanded ? "Show less" : `Show all ${rows.length}`}
            </button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-4 text-sm text-slate-500 font-[Outfit] italic">
          No unsubscribes yet — everyone is still receiving campaign emails.
        </div>
      ) : (
        <ul className="divide-y divide-slate-50">
          {visible.map((u, i) => (
            <li key={u.email} data-testid={`unsub-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] items-center gap-3 px-5 py-3 hover:bg-slate-50/50">
              <div className="min-w-0">
                <div className="font-[Outfit] text-sm font-semibold text-slate-900 truncate">{u.email}</div>
                {u.name && <div className="font-[Outfit] text-[11px] text-slate-500 truncate">{u.name}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {u.lists.map((l) => (
                  <span key={l} className="inline-flex text-[10px] uppercase tracking-wider font-[Outfit] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {l}
                  </span>
                ))}
                <span className="text-[11px] font-[Outfit] text-slate-400 tabular-nums ml-2">{fmtTs(u.latest_at)}</span>
              </div>
              <button
                data-testid={`unsub-remove-${i}`}
                disabled={busy}
                onClick={() => removeForever(u.email)}
                className="text-[11px] font-[Outfit] font-semibold text-rose-700 hover:text-rose-900 disabled:text-slate-300 whitespace-nowrap"
                title="Permanently delete from every campaign list"
              >
                🗑 Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-600 font-[Outfit]">
          💡 Unsubscribed contacts are automatically excluded from all future sends. &ldquo;Remove&rdquo; is only needed if you want the record gone entirely (destructive — cannot undo).
        </div>
      )}
    </div>
  );
}
